<template>
    <div class="association-preview flex column gap-20 align-start">
        <!-- Variant A: 'stepper' — confirm one field at a time -->
        <div class="stepper flex column gap-20 align-start mb-20px" v-if="variant === 'stepper' && (!canBeSaved || errorList.length == 0)">
            <div class="steps flex row gap-10px align-start">
                <div class="step bold" 
                    :class="{ 
                        'selected-column': index === currentStep,
                        'configured': step.isSet,
                        'cursor-pointer': clickable(index)
                    }"
                    :data-type-steper="step.field"
                    @click="selectIndex(index)"
                    v-for="(step, index) in mandatoryFields">
                    {{ index + 1 }}. Select {{ step.label }}
                </div>
            </div>
            <div class="subtext font-size-16 text-left bold">
                Confirm that the column we`ve selected for <span class="selected-label">{{ mandatoryFields[currentStep].label}}</span> correspond to the right fields. Selected column is highlighted in blue. You can change the column by clicking on the header of the table.
            </div>
        </div>
        <div v-if="errorList.length > 0" class="w-full flex column align-start gap-20">
           
            <div class="font-size-14 flex gap-10px align-center">
              <span><b>{{  errorList.length }} error{{ errorList.length > 1 ? 's' : '' }} found in the file.</b> Please fix the errors and try again.</span>
              <button v-if="variant === 'list'" class="button transparent w-content back-btn" @click="backToMatching()">Back to matching</button>
            </div>
            <div class="toggle flex gap-20px align-center toggle-error" v-if="errorList.length">
              <div @click="goBack()" class="flex pointer toggle-elem">
                <img src="@/assets/arrow-white.svg" alt="back" style="width: 20px; transform: rotate(-90deg);" />
              </div>
              <div>Error {{currentError}} of {{ errorList.length }}</div>
              <div @click="goForward()" class="flex pointer toggle-elem">
                <img src="@/assets/arrow-white.svg" alt="back" style="width: 20px; transform: rotate(90deg);" />
              </div>
            </div>

        </div>
        <!-- Variant B: 'list' — summary bar above the mapping list + preview -->
        <div v-if="variant === 'list' && errorList.length == 0" class="list-top w-full flex justify-between align-center gap-20">
            <div class="file-info font-size-14">
                File: <b>{{ props.file?.name }}</b> · {{ headers.length }} columns
            </div>
            <div class="flex align-center gap-10px">
                <span class="chip ok">{{ listStats.matched }} matched</span>
                <span class="chip warn" v-if="listStats.pending">{{ listStats.pending }} to choose</span>
                <span class="chip muted" v-if="listStats.skipped">{{ listStats.skipped }} not in file</span>
                <div class="progress-bar"><i :style="{ width: listStats.percent + '%' }"></i></div>
            </div>
        </div>
        <div v-if="errorList.length == 0" class="grow-area w-full" :class="{ 'match-layout': variant === 'list' }">
            <div v-if="variant === 'list'" class="map-panel">
                <div v-if="hasDateColumns" class="date-format-row map-grid">
                    <span class="map-label">Date format<span class="req">*</span></span>
                    <select class="map-select" :class="{ missing: !dateFormat }" v-model="dateFormat">
                        <option value="">Select date format…</option>
                        <option v-for="option in dateFormatOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                    <span class="map-sample">
                        <span class="sample-text" v-if="firstDateSample">
                            {{ firstDateSample }} → <b>{{ dateFormat ? (parsedDateSample || 'invalid') : '?' }}</b>
                        </span>
                    </span>
                </div>
                <div class="map-head map-grid"><span>Field</span><span>Your column</span><span>Sample</span></div>
                <div class="map-body">
                    <template v-for="group in fieldGroups" :key="group.name">
                        <div class="map-group">{{ group.name }}</div>
                        <div v-for="item in group.items" :key="item.field.field"
                            class="map-row map-grid"
                            :class="{ active: item.index === activeFieldIndex }"
                            @click="activateField(item.index)">
                            <span class="map-label">{{ item.field.label }}<span v-if="item.field.required" class="req">*</span></span>
                            <select class="map-select"
                                :class="selectState(item.field)"
                                :value="selectValue(item.field)"
                                @focus="activateField(item.index)"
                                @change="onSelectColumn(item.field, $event.target.value)">
                                <option value="">Select column…</option>
                                <option v-if="!item.field.required" :value="NOT_IN_FILE">— not in my file —</option>
                                <option v-for="header in headers" :key="header" :value="header">{{ header }}</option>
                            </select>
                            <span class="map-sample">
                                <span class="sample-text">{{ sampleValue(item.field.selectedColumn) }}</span>
                                <span v-if="isAutoMatched(item.field)" class="tag">auto</span>
                            </span>
                        </div>
                    </template>
                </div>
            </div>
            <div class="preview-panel">
                <div v-if="variant === 'list'" class="preview-title font-size-14 flex justify-between gap-10px">
                    <span>Preview (first {{ preview.length }} rows). Click a header to assign it to the selected field.</span>
                    <span v-if="activeField">{{ activeField.label }} → <b>{{ activeField.selectedColumn || '—' }}</b></span>
                </div>
                <div class="w-full overflow-auto table-holder" ref="previewHolder">
            <table class="table sheet-table">
                <thead>
                <tr>
                    <th class="row-num corner"></th>
                    <th v-for="(value, key) in preview[0]"
                        :class=" {
                            'selected-data-column': key === highlightedColumn,
                            'mapped-column': variant === 'list' && columnToField[key]
                        }"
                        :data-type="key"
                        :key="key"
                        @click="handleColumnClick(key)"
                        >
                    {{ key }}
                    <small v-if="variant === 'list' && columnToField[key]" class="mapped-label">{{ columnToField[key] }}</small>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, index) in preview" :key="index">
                    <td class="row-num">{{ index + 1 }}</td>
                    <td v-for="(value, key) in row" :key="key"
                        :class=" {
                            'selected-data-column': key === highlightedColumn
                        }">
                        <div class="w-fit">
                            {{ value }}
                        </div>
                    </td>
                </tr>
                <tr v-for="n in FILLER_ROWS" :key="'filler-' + n" class="filler-row">
                    <td class="row-num">{{ preview.length + n }}</td>
                    <td v-for="key in headers" :key="key"
                        :class="{ 'selected-data-column': key === highlightedColumn }"></td>
                </tr>
                </tbody>
            </table>
                </div>
            </div>
        </div>
        <div v-else class="w-full overflow-auto table-holder grow-area" >    
            <table class="table sheet-table">
                <thead>
                <tr>
                    <th class="row-num corner"></th>
                    <th v-for="key in dataKeys(errorRows[0])" :key="key" >
                    {{ key }}
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, indexRow) in errorRows" :key="indexRow">
                    <td class="row-num">{{ row.index + 1 }}</td>
                    <td v-for="key in dataKeys(row)" :key="key" :class="{ 'error-td': findError(row.index, key) }">
                        <div v-if="findError(row.index, key)" class="flex gap-5px align-center relative" :data-error-key="'error-' + indexRow + '-' + key.replaceAll(' ', '-')">
                            <div class="error-cell">{{ findError(row.index, key) }}</div>
                            {{ row[key] }}
                        </div>
                        <div v-else class="w-fit">
                            {{ row[key] }}
                        </div>
                    </td>
                </tr>
                <tr v-for="n in FILLER_ROWS" :key="'filler-' + n" class="filler-row">
                    <td class="row-num"></td>
                    <td v-for="key in dataKeys(errorRows[0])" :key="key"></td>
                </tr>
                </tbody>
            </table>
        </div>        
        <div class="list-footer w-full flex justify-between align-center gap-20" v-if="variant === 'list' && errorList.length == 0">
            <span v-if="firstPendingField" class="hint">
                “{{ firstPendingField.label }}” has no column yet. Pick one<template v-if="!firstPendingField.required"> or mark it “not in my file”</template>.
            </span>
            <span v-else-if="hasDateColumns && !dateFormat" class="hint">
                Choose how dates are written in your file (e.g. is 01/08/2019 the 1st of August or January 8th?).
            </span>
            <span v-else></span>
            <button class="button w-content" :disabled="!canBeSaved" @click="importData()" :style="'user-select: none;'">Validate &amp; Upload</button>
        </div>
        <div class="w-full flex justify-center mt-20px" v-if="variant === 'stepper' && errorList.length == 0">
            <button
                v-if="!canBeSaved" 
                class="button w-content" 
                :disabled="currentSelectedColumn === null"
                @click="confirmSelect()"
                :style="'user-select: none;'"
                >
                Confirm
            </button>
            <button v-else class="button w-content" 
            :style="'user-select: none;'"
            @click="importData()">Upload Player Data</button>
        </div>
        <PopupCover v-if="showImportProgressPopup" @close="closeImportProgressPopup()">
            <PopupBody :width="'400px'" :height="'200px'" @close="closeImportProgressPopup()">
                <template #header>
                    Import in progress
                </template>
                <template #body>
                  
                    <div class="flex column gap-20 align-center justify-center h-full" v-if="importStatus === 'completed'">
                        <div class="font-size-16 text-center">
                            Your file has been imported successfully!
                        </div>
                        <button class="button" @click="closeImportProgressPopup(true)">Close</button>
                    </div>
                    <div class="flex column gap-20 align-center justify-center h-full" v-else-if="importStatus === 'failed'">
                        <div class="font-size-16 text-center">
                            There was an error importing your file. Please try again later or contact support.
                        </div>
                        <button class="button" @click="closeImportProgressPopup(true)">Close</button>
                    </div>
                    <div class="flex column gap-20 align-center justify-center h-full" v-else>
                        <div class="font-size-16 text-center">
                            Your file is being imported. This process can take a while depending on the size of your file. 
                            You can safely close this window and check the status from Import History.
                        </div>
                        <LoaderItem :size="'50px'"/>
                        <div>
                            Imported rows: {{ importedRowsCount }}
                        </div>
                    </div>
                </template>
            </PopupBody>
        </PopupCover>
        <LoaderPopup v-if="validatingData"
            :hideHeader="true"
            :message="'Validating the File...'"
        />

    </div>
</template>

<script setup>
    import { ref, defineEmits, onMounted, computed, nextTick, watch } from 'vue'
    import Papa from 'papaparse';
    import store from '../store';
    import API from '../api';
    import LoaderItem from './LoaderItem.vue';
    import PopupCover from './PopupCover.vue';
    import PopupBody from './PopupBody.vue';
    import LoaderPopup from './LoaderPopup.vue';
    import { validateValue, CSV_DATE_FORMATS, parseCsvDate } from '../../../shared/validation.js'
    import { formatIsoDate } from '@/utils/date_display.js'
    // ---- A/B toggle for the field-matching UI ----
    // 'stepper' — original flow: confirm each field one at a time, every field mandatory
    // 'list'    — all fields at once with dropdowns + live preview; fields with required: false can be marked "not in my file"
    const MATCH_UI_VARIANT = 'list';
    const variant = MATCH_UI_VARIANT;
    const NOT_IN_FILE = '__not_in_file__';
    // empty spreadsheet-style rows shown under the data so short previews don't look empty
    const FILLER_ROWS = 15;

    const props = defineProps(['file']);
    const emit = defineEmits(['importFinished', 'importSent']);
    const preview = ref([]);
    const intervalId = ref(null);
    
    const validatingData = ref(false);
    const errorList = ref([]);

    const currentError = ref(1);


    const errorRows = computed(() => {
        const addedIds = {};
        const uniquList = []
        errorList.value.forEach(error => {
            if (!addedIds[error.index]) {
                uniquList.push({index: error.index, ...error.row});
                addedIds[error.index] = true;
            }
        })
        return uniquList; 
    })

    // extra fielsd in each object
    // isSet: false, selectedColumn: null

    const mandatoryFields = ref([
        { field: 'PlayerId', group: 'Player', required: true, label: 'Player System id', validator_key: 'player_id',
            searchableValues: ['PlayerAccontId', 'Player System id', 'playerid', 'systemid', 'systemId', 'player_id', 'id', 'userid', 'user_id', 'playerid', ]
        }, 
        { field: 'FirstName', group: 'Player', required: true, label: 'First Name', validator_key: 'first_name',
            searchableValues: ['first_name']
        },
        { field: 'LastName', group: 'Player', required: true,label: 'Last Name', validator_key: 'last_name',
            searchableValues: ['last_name', 'surname']
        },
        { field: 'Gender', group: 'Player', required: false, label: 'Gender', validator_key: 'gender_inverse'},
        { field: 'BirthDate', group: 'Player', required: true, label: 'Birth Date', validator_key: 'csv_date',
            searchableValues: ['dob', 'birth_date', 'birthdate', 'date_of_birth', 'dateofbirth']
        },

        { field: 'Nationality', group: 'Player', required: false, label: 'Nationality', searchableValues: [], validator_key: 'iso_code'},
        { field: 'PassportNumber', group: 'Identification', required: false, label: 'Passport Number', searchableValues: [], validator_key: 'identification_number'},
        { field: 'PassportCountry', group: 'Identification', required: false, label: 'Passport Country', searchableValues: [], validator_key: 'iso_code'},
        { field: 'IdNumber', group: 'Identification', required: false, label: 'Id Number', searchableValues: [], validator_key: 'tax_number'},

        { field: 'IdentificationCountry', group: 'Identification', required: false, label: 'Country of Identification Documents', searchableValues: ['document country'], validator_key: 'iso_code'},
        { field: 'IndentificationType', group: 'Identification', required: false, label: 'Indentification Document Type', searchableValues: [], validator_key: 'identification_type'},
        { field: 'IdentificationNumber', group: 'Identification', required: false, label: 'Identification Document Number', searchableValues: [], validator_key: 'identification_number'},
        { field: 'DocumentIssueDate', group: 'Identification', required: false, label: 'Identification Document Issue Date', searchableValues: ['issue date'], validator_key: 'csv_date'},
        { field: 'DocumentExpiryDate', group: 'Identification', required: false, label: 'Identification Document Expiry Date', searchableValues: ['expiry'], validator_key: 'csv_date'},
        
        { field: 'ResidenceCountry', group: 'Player', required: false, label: 'Residence Country', searchableValues: ['residence'], validator_key: 'iso_code'},
        

        { field: 'Country', group: 'Address', required: false, label: 'Country of current address', searchableValues: ['country'], validator_key: 'iso_code'},
        { field: 'City', group: 'Address', required: false, label: 'City', searchableValues: [''], validator_key: 'city'},
        { field: 'Address', group: 'Address', required: false, label: 'Address', searchableValues: ['address'], validator_key: 'address'},
        { field: 'Zip', group: 'Address', required: false, label: 'Zip Code', searchableValues: ['zipcode', 'postal code', 'postalcode'], validator_key: 'zip'},

    ]);


    ///////////////////////////////////////
    // date format — the user picks how dates in the file are written
    ///////////////////////////////////////
    const dateFormatOptions = Object.entries(CSV_DATE_FORMATS).map(([value, spec]) => ({ value, label: spec.label }));
    // '' = not chosen yet (the preview was ambiguous, e.g. 01/08/2019 fits both dd/mm and mm/dd)
    const dateFormat = ref('');
    const dateFields = computed(() => mandatoryFields.value.filter(field => field.validator_key === 'csv_date'));
    const hasDateColumns = computed(() => dateFields.value.some(field => !field.skipped && field.selectedColumn));
    const dateSamples = computed(() => {
        const samples = [];
        dateFields.value.forEach(field => {
            if (field.skipped || !field.selectedColumn) return;
            preview.value.forEach(row => {
                const value = `${row[field.selectedColumn] ?? ''}`.trim();
                if (value) samples.push(value);
            });
        });
        return samples;
    });
    // pre-select the format only when exactly one format parses every sample
    function guessDateFormat() {
        if (!dateSamples.value.length) return;
        const fitting = Object.keys(CSV_DATE_FORMATS).filter(format => dateSamples.value.every(value => parseCsvDate(value, format)));
        dateFormat.value = fitting.length === 1 ? fitting[0] : '';
    }
    // when the user (re)maps a date column, re-guess unless they already chose a format that still fits
    watch(dateSamples, () => {
        if (dateFormat.value && dateSamples.value.every(value => parseCsvDate(value, dateFormat.value))) return;
        guessDateFormat();
    });
    function dateValidatorKey(field) {
        return field.validator_key === 'csv_date' ? CSV_DATE_FORMATS[dateFormat.value]?.validatorKey || 'csv_date' : field.validator_key;
    }
    const firstDateSample = computed(() => dateSamples.value[0] || '');
    // shown in the browser's date format, like the rest of the app
    const parsedDateSample = computed(() => {
        const iso = firstDateSample.value && dateFormat.value ? parseCsvDate(firstDateSample.value, dateFormat.value) : null;
        return iso ? formatIsoDate(iso) : null;
    });

    const currentStep = ref(0);
    const currentSelectedColumn = ref(null);
    const showImportProgressPopup = ref(false);
    const importId = ref(null);
    const importedRowsCount = ref(0);
    const importStatus = ref('pending');
    const columnMatchSuggestion = ref(null);
    onMounted(async () => {
        await loadData();
        columnMatchSuggestion.value = autoMapColumns(Object.keys(preview.value[0]), mandatoryFields.value);
        guessDateFormat();
        setSuggestion();

    })
    function setSuggestion(scrollIntoView = false){ 
        if(mandatoryFields.value[currentStep.value].isSet) {
            return
        }
        const suggestion = columnMatchSuggestion.value[mandatoryFields.value[currentStep.value].field];
        if (!suggestion) {
            return               
        }
        currentSelectedColumn.value = suggestion;
        if (scrollIntoView) {
            setTimeout(() => {
                const headerElement = document.querySelector('[data-type="' + suggestion + '"]');
                const stepperElement = document.querySelector('[data-type-steper="' + mandatoryFields.value[currentStep.value].field + '"]');
                if (headerElement) {
                    headerElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center'  });
                }
                if (stepperElement) {
                    stepperElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center'  });
                }

            }, 100)
        }   
    }


    function handleColumnClick(key) {
        if (variant === 'list') {
            if (!activeField.value) return;
            activeField.value.selectedColumn = key;
            activeField.value.skipped = false;
            return;
        }
        currentSelectedColumn.value = key;
    }

    ///////////////////////////////////////
    // 'list' variant helpers
    ///////////////////////////////////////
    const activeFieldIndex = ref(0);
    const previewHolder = ref(null);
    const activeField = computed(() => mandatoryFields.value[activeFieldIndex.value]);
    const headers = computed(() => Object.keys(preview.value[0] || {}));

    // a field is resolved when it has a column, or is optional and explicitly marked "not in my file"
    function fieldResolved(field) {
        return field.skipped ? !field.required : !!field.selectedColumn;
    }
    const firstPendingField = computed(() => mandatoryFields.value.find(field => !fieldResolved(field)));

    const listStats = computed(() => {
        const total = mandatoryFields.value.length;
        const skipped = mandatoryFields.value.filter(field => field.skipped).length;
        const matched = mandatoryFields.value.filter(field => !field.skipped && field.selectedColumn).length;
        return { matched, skipped, pending: total - matched - skipped, percent: Math.round((matched + skipped) / total * 100) };
    });

    const fieldGroups = computed(() => {
        const groups = [];
        mandatoryFields.value.forEach((field, index) => {
            let group = groups.find(g => g.name === field.group);
            if (!group) {
                group = { name: field.group, items: [] };
                groups.push(group);
            }
            group.items.push({ field, index });
        });
        return groups;
    });

    // column header -> label(s) of the field(s) mapped to it
    const columnToField = computed(() => {
        const map = {};
        mandatoryFields.value.forEach(field => {
            if (field.skipped || !field.selectedColumn) return;
            map[field.selectedColumn] = map[field.selectedColumn] ? `${map[field.selectedColumn]}, ${field.label}` : field.label;
        });
        return map;
    });

    const highlightedColumn = computed(() => {
        if (variant === 'list') {
            return activeField.value && !activeField.value.skipped ? activeField.value.selectedColumn : null;
        }
        return currentSelectedColumn.value;
    });

    function activateField(index) {
        activeFieldIndex.value = index;
        scrollToColumn(mandatoryFields.value[index].selectedColumn);
    }
    function scrollToColumn(column) {
        if (!column) return;
        nextTick(() => {
            const header = previewHolder.value?.querySelector(`[data-type="${CSS.escape(column)}"]`);
            if (header) {
                header.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        });
    }
    function selectValue(field) {
        return field.skipped ? NOT_IN_FILE : (field.selectedColumn || '');
    }
    function onSelectColumn(field, value) {
        if (value === NOT_IN_FILE) {
            field.skipped = true;
            field.selectedColumn = null;
        } else {
            field.skipped = false;
            field.selectedColumn = value || null;
            scrollToColumn(field.selectedColumn);
        }
    }
    function isAutoMatched(field) {
        return !field.skipped && !!field.selectedColumn && field.selectedColumn === field.autoColumn;
    }
    function selectState(field) {
        return {
            auto: isAutoMatched(field),
            none: field.skipped,
            missing: !fieldResolved(field),
        };
    }
    function sampleValue(column) {
        if (!column) return '';
        const row = preview.value.find(r => r[column] !== undefined && r[column] !== null && `${r[column]}`.trim() !== '');
        return row ? row[column] : '';
    }
    function backToMatching() {
        errorList.value = [];
        currentError.value = 1;
    }

    function confirmSelect(){
        if (!currentSelectedColumn.value){
            store.dispatch('addNotification', {type: 'error', text: 'Please select a column before confirming'});
            return
        }
        mandatoryFields.value[currentStep.value].isSet = true;
        mandatoryFields.value[currentStep.value].selectedColumn = currentSelectedColumn.value;
        currentSelectedColumn.value = null;
        if(currentStep.value < mandatoryFields.value.length - 1){
            currentStep.value++;
        }
        setSuggestion(true)
    }


    ///////////////////////////////////////
    ///////////////////////////////////////
    ///////////////////////////////////////
    ///////////////////////////////////////

    function normalize(str) {
        return str
            .toLowerCase()
            .replace(/[_\-]/g, ' ')
            .replace(/[^\w\s]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        }

    function similarity(a, b) {
        a = normalize(a);
        b = normalize(b);

        if (a === b) return 1;

        if (a.includes(b) || b.includes(a)) return 0.85;

        const aWords = a.split(' ');
        const bWords = b.split(' ');

        const overlap = aWords.filter(word => bWords.includes(word)).length;

        return overlap / Math.max(aWords.length, bWords.length);
    }

    /**
     * Auto maps CSV headers to your mandatory fields
     * @param {Array<string>} headers - CSV headers
     * @param {Array} fields - your mandatoryFields ref.value
     */
    function autoMapColumns(headers, fields) {
        const usedHeaders = new Set();
        const nameMapDict = {}
        for (let field of fields) {
            let bestMatch = null;
            let bestScore = 0;

            const possibleNames = [
                field.label,
                field.field,
                ...(field.searchableValues || [])
            ];

            for (let header of headers) {
                if (usedHeaders.has(header)) continue;
                for (let name of possibleNames) {
                    const score = similarity(header, name);
                    if (score > bestScore) {
                        bestScore = score;
                        bestMatch = header;
                    }
                }
            }
            if (bestScore > 0.5) {
                nameMapDict[field.field] = bestMatch;
                field.selectedColumn = bestMatch;
                field.autoColumn = bestMatch;
                usedHeaders.add(bestMatch);
            } else {
                field.selectedColumn = null;
                field.autoColumn = null;
            }
        }
        return nameMapDict;
    }

    ///////////////////////////////////////
    ///////////////////////////////////////
    ///////////////////////////////////////
    ///////////////////////////////////////

    const clickable = computed(() => (index) => {
        if (index < currentStep.value) {
            return true;
        }
        if (index != 0 && mandatoryFields.value[index - 1].isSet) {
            return true
        }
        return false
    })
    const canBeSaved = computed(() => {
        if (variant === 'list') {
            return mandatoryFields.value.every(fieldResolved) && (!hasDateColumns.value || !!dateFormat.value);
        }
        return mandatoryFields.value.every(field => field.isSet);
    })
    function selectIndex(index){
        
        if (mandatoryFields.value[index].isSet) {
            currentSelectedColumn.value = mandatoryFields.value[index].selectedColumn;
            currentStep.value = index;
        }
        if (index != 0 && mandatoryFields.value[index - 1].isSet) {
            currentSelectedColumn.value = mandatoryFields.value[index].selectedColumn;
            currentStep.value = index;
        }
        setSuggestion()
    }

    async function loadData(){
        return new Promise((resolve) => {
            let rowCount = 0;
            Papa.parse(props.file, {
                header: true,
                skipEmptyLines: true,
                worker: true,
                step: function (result, parser) {
                    preview.value.push(result.data);
                    rowCount++;
                    if (rowCount >= 10) {
                        resolve();
                        parser.abort(); 
                    }
                },
                complete: function () {
                    resolve();
                },
            });
        })
    }


    async function importData() {
        
        if (!props.file) return;
        validatingData.value = true;
        const errors = await validateTheData();
        validatingData.value = false;
        errorList.value = errors;
        console.log('errors', errors);
        if (errors.length) {

            return;
        }

        const mappingColumns = mandatoryFields.value.reduce((acc, field) => {
            acc[field.field] = field.skipped ? null : field.selectedColumn;
            return acc;
        }, {})

        const response = await API.importPlayerData(props.file, mappingColumns, dateFormat.value || 'yyyy-mm-dd');
        emit('importSent');
        importId.value = response.id;
        store.dispatch('addNotification', {text: `Your file ${props.file.name} is being imported`, type: 'success'});
        showImportProgressPopup.value = true;
        initiateImportStatusCheck();
    } 
    function closeImportProgressPopup(emitEvent = false) {
        showImportProgressPopup.value = false;
        importedRowsCount.value = 0;
        if (intervalId.value) {
            clearInterval(intervalId.value);
        }
        if (emitEvent) {
            emit('importFinished', importStatus.value);
        }
        importStatus.value = null;
    }
    function initiateImportStatusCheck() {
        intervalId.value = setInterval(async () => {
            const response = await API.checkImportStatus(importId.value);
            if (response.status === 'completed') {
                clearInterval(intervalId.value);
                importStatus.value = 'completed';
                store.dispatch('addNotification', {text: `Your file ${props.file.name} has been imported successfully`, type: 'success'});
            } else if (response.status === 'failed') {
                clearInterval(intervalId.value);
                importStatus.value = 'failed';
                store.dispatch('addNotification', {text: `There was an error importing your file ${props.file.name}`, type: 'error'});
            } else {
                importedRowsCount.value = response.importedRows;
            }
        }, 2000);
    }


    async function validateTheData(){
        const errorList = [];

        return new Promise((resolve) => {
            let rowCount = 0;
            Papa.parse(props.file, {
                header: true,
                skipEmptyLines: true,
                worker: true, 
                step: function (result) {
                    validateRow(result.data, rowCount, errorList);
                    rowCount++;
                },
                complete: function () {
                    resolve(errorList);
                },
            });
        })
    }

    function validateRow(row, index, errorList) {

        for (let field of mandatoryFields.value) {
            // 'list' variant: optional fields marked "not in my file" have no column to validate
            if (field.skipped || !field.selectedColumn) continue;
            const key = field.selectedColumn;
            const valueToSend = row[field.selectedColumn];
            
            const datatoSend = {
                key: dateValidatorKey(field),
                name: key, 
                value: valueToSend, 
                errorList: errorList, 
                index: index, 
                // 'list' variant: empty cells are allowed in optional columns
                skipp_null: variant === 'list' ? !field.required : field.skip_null, 
                row: row
            }
            validateValue(datatoSend, true);
        }
    }

    function goBack(){
      if (currentError.value > 1) {
        currentError.value -= 1;
        const error = errorList.value[currentError.value - 1];
        const element = document.querySelector(`[data-error-key="error-${error.index}-${error.columnName.replaceAll(' ', '-')}"]`);
        animate(element);
      }
    }
    function goForward(){
      if (currentError.value < errorList.value.length) {
        currentError.value += 1;
        const error = errorList.value[currentError.value - 1];
        const element = document.querySelector(`[data-error-key="error-${error.index}-${error.columnName.replaceAll(' ', '-')}"]`);
        animate(element);
      }
    }
    // errorRows carry a synthetic `index` key; it is shown in the row-number gutter instead
    function dataKeys(row) {
        return row ? Object.keys(row).filter(key => key !== 'index') : [];
    }
    function findError(indexRow, column) {
    
        const error = errorList.value.find(item => item.index == indexRow && item.columnName == column);
        console.log('finding error for', indexRow, column, error);
        if (error) {
            return `Error: ${error.errorText}`;
        }
        return '';
    }
    function animate(element) {
      if (!element) {
        return;
      }
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      element.parentNode.classList.add('highlight');
      setTimeout(() => {
        element.parentNode.classList.remove('highlight');
      }, 2000);
    }

</script>

<style scoped lang="scss">
    /* Fills the (near full-screen) popup body; only the table / field list scroll */
    .association-preview {
        flex: 1;
        min-height: 0;
        overflow: hidden;
    }
    .grow-area {
        flex: 1;
        min-height: 0;
        &:not(.match-layout) {
            display: flex;
            flex-direction: column;
        }
    }
    .table-holder {
        max-width: 100%;
        overflow: auto;
    }
    .preview-panel {
        display: flex;
        flex-direction: column;
        flex: 1;
        height: 100%;
        min-height: 0;
        .table-holder {
            flex: 1;
            min-height: 0;
        }
    }
    /* ---- 'list' variant ---- */
    .list-top, .match-layout, .list-footer {
        width: 100%;
    }
    .file-info {
        color: #555;
        b { color: black; }
    }
    .chip {
        padding: 3px 10px;
        border-radius: 12px;
        font-size: 13px;
        white-space: nowrap;
        &.ok { background: #e3f4e8; color: #1e7b3a; }
        &.warn { background: #fff3c4; color: #7a5b00; }
        &.muted { background: #eee; color: #666; }
    }
    .progress-bar {
        width: 160px;
        height: 8px;
        background: rgb(224 226 236);
        border-radius: 4px;
        overflow: hidden;
        i {
            display: block;
            height: 100%;
            background: rgb(0 115 239);
            transition: width .2s;
        }
    }
    .match-layout {
        display: grid;
        grid-template-columns: 520px minmax(0, 1fr);
        gap: 20px;
        align-items: stretch;
    }
    .map-panel {
        border: 1px solid #ddd;
        border-radius: 8px;
        overflow: hidden;
        text-align: left;
        background: white;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }
    .map-grid {
        display: grid;
        grid-template-columns: 1.3fr 1.2fr .9fr;
        gap: 10px;
        align-items: center;
        padding: 0 12px;
    }
    .map-head {
        background: #f3f3f3;
        height: 34px;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: .03em;
        color: #666;
        border-bottom: 1px solid #ddd;
    }
    .map-body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
    }
    .map-group {
        background: #fafafa;
        padding: 6px 12px;
        font-size: 12px;
        font-weight: 600;
        color: #888;
        text-transform: uppercase;
        letter-spacing: .03em;
        border-bottom: 1px solid #eee;
    }
    .map-row {
        min-height: 40px;
        border-bottom: 1px solid #eee;
        font-size: 14px;
        color: black;
        cursor: pointer;
        &:hover { background: #fafcff; }
        &.active {
            background: #eef5ff;
            box-shadow: inset 3px 0 0 rgb(0 115 239);
        }
    }
    .date-format-row {
        min-height: 44px;
        border-bottom: 1px solid #ddd;
        background: #fafcff;
        font-size: 14px;
        color: black;
    }
    .req {
        color: #D06A69;
        margin-left: 2px;
    }
    .map-select {
        width: 100%;
        height: 28px;
        padding: 0 6px;
        border: 1px solid #ccc;
        border-radius: 5px;
        background: white;
        font-size: 13px;
        color: black;
        &.auto { border-color: #9cc3f5; }
        &.none { border-style: dashed; color: #999; }
        &.missing { border-color: #e0a100; background: #fffaf0; color: #7a5b00; }
    }
    .map-sample {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        font-size: 13px;
        color: #666;
        .sample-text {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
    .tag {
        flex: none;
        font-size: 10px;
        padding: 1px 6px;
        border-radius: 8px;
        background: #e3efff;
        color: #1f4f99;
    }
    .match-layout .preview-panel {
        min-width: 0;
        .table-holder {
            border: 1px solid #ddd;
            border-radius: 8px;
        }
    }
    .preview-title {
        color: #555;
        margin-bottom: 8px;
        text-align: left;
        b { color: black; }
    }
    .sheet-table th.mapped-column:not(.selected-data-column) {
        border-bottom: 2px solid #9cc3f5;
    }
    .mapped-label {
        display: block;
        margin-top: 2px;
        font-size: 10px;
        color: #1f4f99;
    }
    .list-footer {
        border-top: 1px solid #eee;
        padding-top: 16px;
    }
    .hint {
        font-size: 13px;
        color: #7a5b00;
        background: #fff3c4;
        padding: 6px 10px;
        border-radius: 5px;
        text-align: left;
    }
    .back-btn {
        padding: 5px 12px;
    }

    /* Mirrors the jspreadsheet look used by draft sheets (FiuGrid / SheetView) */
    .sheet-table {
        border-collapse: separate;
        border-spacing: 0;
        background-color: #fff;
        font-size: 13px;
        text-align: left;
        color: black;
        border-right: 1px solid #ccc;
        border-bottom: 1px solid #ccc;
        td, th {
            border-top: 1px solid #ccc;
            border-left: 1px solid #ccc;
            padding: 4px 6px;
            height: 26px;
            line-height: 1em;
            white-space: nowrap;
            box-sizing: border-box;
        }
        th {
            position: sticky;
            top: 0;
            z-index: 2;
            background-color: #f3f3f3;
            font-weight: normal;
            text-align: center;
            cursor: pointer;
            min-width: 100px;
            &:not(.selected-data-column):not(.corner):hover {
                background-color: #dcdcdc;
            }
        }
        .row-num {
            position: sticky;
            left: 0;
            z-index: 1;
            background-color: #f3f3f3;
            text-align: center;
            min-width: 50px;
            width: 50px;
            color: #555;
            cursor: default;
        }
        th.corner {
            z-index: 3;
        }
        td.selected-data-column {
            background-color: rgba(0, 115, 239, .08);
        }
        th.selected-data-column {
            background-color: #dcdcdc;
            border-bottom: 2px solid #007BFF;
        }
        .filler-row td:not(.row-num) {
            cursor: default;
        }
        td.error-td {
            background-color: #f6d3d3;
        }
    }
    .stepper {
        width: 100%;
    }
    .steps {
        // flex-wrap: wrap;
        overflow-x: auto;
        width: 100%;

    }
    .step {
        background-color: #c8c8c8;
        padding: 10px 15px;
        border-radius: 5px;
        white-space: nowrap;
    }
    .selected-column {
        background-color: #007BFF;
        color: white;
    }
    .selected-data-column {
        background-color: #007bff8d;
        // color: white;s
    }
    .selected-label {
        color: #007BFF;
        text-decoration: underline;
        font-size: 17px;
    }
    .configured:not(.selected-column) {
        background-color: #007bff8d;
        color: white;
        opacity: 0.8;
    }
    .cursor-pointer {
        cursor: pointer;
    }
    .error-cell {
      background-color: #D06A69;
      color: white;
      padding: 0px 10px;
      border-radius: 5px;
      // position: absolute ;
    }
    .toggle-error {
      background-color: #D06A69;
      color: white;
      padding: 5px;
      border-radius: 5px;
      gap: 10px;
    }
    .highlight {
      animation: highlightAnimation 2s ease-in-out;
    }
    .toggle-elem {
      padding: 5px;
      border-radius: 5px;
      &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      }
    }
</style>